import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { Helmet } from 'react-helmet-async';
import { Trans } from 'react-i18next';
import { t } from 'i18next';
import { Button, RoundBadge } from '@peacecover/component';
import { PeopleFill } from '@peacecover/icon';
import { getLanguage } from '../../utils/etcUtils';
import saveData from '../../utils/saveData';
import { isEmpty } from 'lodash';
import { useColorMode } from '@chakra-ui/react';
import HorizontalScroll from '../my-page/components/HorizontalScroll';
import NotificationBadge from '../my-page/components/NotificationBadge';
import useChatRoomsQuery from '../../components/api/queries/useChatRoomsQuery';
import ChatItem from './components/ChatItem';
import HomeBottomNav from '../../layout/HomeBottomNav';
import LinkCardItem from './components/LinkCardItem';
import ChatItemListSkeleton from './components/ChatItemListSkeleton';
import type { ChatRoomType, InfiniteChatRoomsData } from './types/ChatMessageType';
import useUserQuery from '../../components/api/queries/useUserQuery';
import useRefreshJwtQuery from '../../components/api/queries/useRefreshJwtQuery';
import NavigateArtistSelectButton from '../select-artist/NavigateArtistSelectButton';
import useArtistQueryLikedOnly from '../hooks/queries/useArtistQueryLikedOnly';
import i18n from '../../i18n/i18n';
import { ReactComponent as PlusProfile } from '../../icons/plus.profile.svg';

const MESSAGE_CATEGORIES = ['All', 'DM', 'Selling', 'Purchasing', 'Exchanging'];

const ChatMainPage = () => {
  const [chatRoomsCategory, setChatRoomsCategory] = useState(MESSAGE_CATEGORIES[0]);
  const [paginatedChatRooms, setPaginatedChatRooms] = useState<ChatRoomType[]>();
  const [isRequestArtistSelectPopupOpen, setIsRequestArtistSelectPopupOpen] = useState(false);
  const [isBettiePromotionPopupOpen, setIsBettiePromotionPopupOpen] = useState(
    localStorage.getItem('isBettiePromotionPopupClosed') !== 'true'
  );
  const { colorMode } = useColorMode();
  const { data, isFetching, fetchNextPage, hasNextPage } = useChatRoomsQuery({
    type: chatRoomsCategory,
    tradingPhotoCardId: undefined,
    onlyJoined: true
  });

  const { data: likedArtists, isLoading: isLoadingLikedArtists } = useArtistQueryLikedOnly();

  const navigate = useNavigate();
  const user = useUserQuery();

  useEffect(() => {
    if (isLoadingLikedArtists) return;
    if (likedArtists && likedArtists.length === 0) {
      setIsRequestArtistSelectPopupOpen(true);
    } else {
      setIsRequestArtistSelectPopupOpen(false);
    }
  }, [likedArtists, isLoadingLikedArtists]);

  const authToken = useRefreshJwtQuery();
  if (authToken?.jwt) {
    saveData('token', JSON.stringify(authToken?.jwt));
  }

  useEffect(() => {
    if (user) {
      saveData('userId', user?.userId);
      saveData('email', user?.email);
    }
    if (user && !isEmpty(user.locale) && getLanguage() !== user.locale) {
      saveData('i18nextLng', user.locale);
      i18n.changeLanguage(user.locale);
    }
  }, [user]);

  useEffect(() => {
    localStorage.removeItem('latestPostId');
  }, []);

  const handlePaginatedChatRooms = useCallback((paginatedData: InfiniteChatRoomsData) => {
    const chatRooms = paginatedData.pages?.flatMap((page) => page.chatRooms) ?? [];
    const sorted = [...chatRooms].sort((a, b) => {
      const aTime = a?.lastMessage?.sentAt ? new Date(a.lastMessage.sentAt).getTime() : 0;
      const bTime = b?.lastMessage?.sentAt ? new Date(b.lastMessage.sentAt).getTime() : 0;
      return bTime - aTime;
    });
    setPaginatedChatRooms(sorted);
  }, []);

  const loadMore = () => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  };

  const handleNavigateArtistSelectButton = () => {
    if (user.baseUserProfile?.nickname) {
      navigate('/search-artists', { replace: true });
    } else {
      navigate('/nickname-setting?init=true', { replace: true });
    }
    setIsRequestArtistSelectPopupOpen(false);
  };

  useEffect(() => {
    if (!isEmpty(data)) {
      handlePaginatedChatRooms(data);
    }
  }, [data, handlePaginatedChatRooms]);

  const existingBettie = paginatedChatRooms?.filter(
    (chatRoom) => chatRoom.subjectType === 'MemberPersona'
  )[0];

  const handleNavigateToBettieSetting = () => {
    if (isFetching) return;

    if (existingBettie) {
      navigate('/bettie-chat-list');
    } else {
      navigate('/bettie-setting?isFirst=true');
    }
  };

  const isLikeSpecificArtist = (artistId: string) => {
    if (!likedArtists) return false;
    return likedArtists.some((artist) => artist.artistId === artistId);
  };
  
  return (
    <div className="min-h-screen pt-[calc(env(safe-area-inset-top))] pb-[calc(120px+env(safe-area-inset-bottom))] bg-background">
      <Helmet>
        <title>{t('tabs.chat')}</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <header className="flex items-center justify-between px-[24px] py-[11px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1">{t('ChatPage.title')}</h1>
        <NotificationBadge />
      </header>
      <HorizontalScroll>
        <div className="flex flex-row gap-[5px] px-[20px] pb-[10px] pt-[11px]">
          <LinkCardItem
            title="Bettie"
            description="ChatPage.bettie.description"
            className="text-white"
            onClick={handleNavigateToBettieSetting}
            style={{
              background:
                'radial-gradient(53.03% 53.03% at 50% 50%, rgba(255, 255, 255, 0.88) 0%, rgba(255, 255, 255, 0.00) 100%), conic-gradient(from 270deg at 50% 50%, #94A5F9 0deg, #5CDBF6 108.00000429153442deg, #FFE175 180deg, #D671F6 288.0000042915344deg, #9F9DF9 360deg)'
            }}
            rightContent={
              <div className="absolute right-[12px] top-[12px]">
                <img
                  src="/assets/card_banner/bettie_banner_image.png"
                  alt="bettie banner"
                  className="w-[38px] h-[43px]"
                />
              </div>
            }
          />
          <LinkCardItem
            title="ChatPage.event.title"
            description="ChatPage.event.description"
            navigateTo="https://fanhearts.com/campaign/?isMobile=true"
            className="bg-cover bg-center bg-no-repeat text-main-light-2"
            style={{
              backgroundImage: "url('/assets/card_banner/campaign_banner_image.png')"
            }}
          />
          <LinkCardItem
            title="ChatPage.promotion.title"
            description="ChatPage.promotion.description"
            navigateTo="/lounge"
            className="bg-[linear-gradient(180deg,#A2CCF9_0%,_#769BFF_100%)] text-white"
            rightContent={
              <div className="w-[41px] h-[38px] absolute right-[17px] top-[13px]">
                <img
                  src="/assets/card_banner/promotion_banner_image.png"
                  alt="promotion banner"
                  className="mix-blend-overlay text-[#97BFFB]"
                />
              </div>
            }
          />
          <LinkCardItem
            title="ChatPage.fanBuddy.title"
            description="ChatPage.fanBuddy.description"
            navigateTo={isEmpty(user?.mbti) ? '/fan-buddy/intro' : '/fan-buddy/find'}
            className="bg-[linear-gradient(180deg,_#FF7FAA_0%,_#FF558F_100%)] text-white"
            rightContent={
              <div className="w-[50px] h-[38px] absolute right-[11px] top-[9px]">
                <img
                  src="/assets/card_banner/fanbuddy_banner_image.png"
                  alt="fanbuddy banner"
                  className="mix-blend-overlay text-[#FF1464]"
                />
              </div>
            }
          />
        </div>
      </HorizontalScroll>
      <HorizontalScroll>
        <div className="flex gap-[8px] px-[20px] py-[8px]">
          {MESSAGE_CATEGORIES.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => {
                setPaginatedChatRooms(undefined);
                setChatRoomsCategory(category);
              }}
              className={`rounded-[32px] px-[12px] py-[8px] text-[14px] font-semibold leading-none border transition-colors
                ${
                  category === chatRoomsCategory
                    ? 'bg-text-main-lv2 text-pure-white dark:text-pure-black border-transparent'
                    : 'bg-on-primary dark:bg-transparent text-text-main-lv2 border-separator1'
                }
              `}
            >
              {t(`notifications.messageCategory${category}`)}
            </button>
          ))}
        </div>
      </HorizontalScroll>

      {/* 메시지 목록 */}
      <div className="pb-[24px]">
        {paginatedChatRooms === undefined ? (
          <ChatItemListSkeleton />
        ) : (
          <>
            <ChatItem variant="lounge" categoryGroup="community" />
            {isLikeSpecificArtist('nctwish') && (
              <ChatItem variant="lounge" categoryGroup="nctwish" />
            )}
            {!existingBettie && <BettieEntryCard />}
            {paginatedChatRooms.length > 0 && (
              <Virtuoso
                useWindowScroll
                overscan={480}
                defaultItemHeight={72}
                style={{ minHeight: 100 }}
                data={paginatedChatRooms ?? []}
                endReached={loadMore}
                itemContent={(_index, chatRoom: ChatRoomType) => (
                  <ChatItem variant="chat" chatRoom={chatRoom} />
                )}
              />
            )}
          </>
        )}
      </div>
      {isRequestArtistSelectPopupOpen && (
        <RequestArtistSelectPopup onClick={handleNavigateArtistSelectButton} />
      )}
      {isBettiePromotionPopupOpen && (
        <BettiePromotionPopup onClose={() => setIsBettiePromotionPopupOpen(false)} />
      )}
      <HomeBottomNav tabIndex={2} />
    </div>
  );
};

export default ChatMainPage;

const RequestArtistSelectPopup = ({ onClick }: { onClick: () => void }) => {
  const { colorMode } = useColorMode();
  return (
    <div
      className="min-h-screen fixed inset-0 z-[1000] flex flex-col items-center justify-center"
      style={{
        backgroundImage: `url(/assets/force_to_update/bg_ask_update_${colorMode}.png)`,
        backgroundRepeat: 'round'
      }}
    >
      <div className="flex flex-col items-center justify-center gap-[11px]">
        <img
          src="/assets/icons/collections/Milky Way.png"
          alt="milky way"
          className="w-[160px] h-[160px]"
        />
        <div className="flex flex-col px-[24px] py-[10px] gap-[14px]">
          <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] text-center">
            {t('requestArtistSelectPopup.title')}
          </h1>
          <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px] text-center whitespace-pre-line">
            {t('requestArtistSelectPopup.description')}
          </p>
        </div>
      </div>
      <div className="mt-[26px]">
        <NavigateArtistSelectButton onClick={onClick} />
      </div>
    </div>
  );
};

const BettieEntryCard = () => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate('/bettie-setting?isFirst=true')}
      className="flex items-center gap-[12px] px-[20px] py-[12px]"
    >
      <div className="relative flex items-center justify-center">
        <PlusProfile width={46} height={46} className="[&>path]:fill-[#B2A3FF]" />
        <div className="z-10 absolute bottom-0 left-[35px] bg-[linear-gradient(90deg,#FF45A9_0%,#FF6A6A_100%)] rounded-full p-[2px] border border-pure-white dark:border-pure-black">
          <PeopleFill size={10} className="text-pure-white" />
        </div>
      </div>
      <div className="flex flex-col items-start gap-[4px]">
        <div className="flex items-center gap-[5px]">
          <p className="text-[15px] font-semibold text-text-main-lv2 leading-[18px]">
            {t('ChatPage.bettieEntryCard.title')}
          </p>
          <RoundBadge className="h-[17px] text-[10px] font-semibold px-[6px] py-[2.5px]">
            {t('ChatPage.category.bettie')}
          </RoundBadge>
        </div>
        <p className="text-[13px] text-text-main-lv3 leading-[16px]">
          {t('ChatPage.bettieEntryCard.startButton')}
        </p>
      </div>
    </button>
  );
};

const BettiePromotionPopup = ({ onClose }: { onClose: () => void }) => {
  const handleCloseForever = () => {
    saveData('isBettiePromotionPopupClosed', true);
    onClose();
  };

  return (
    <div className="fixed top-0 left-0 w-full min-h-screen bg-black/50 z-[1000] flex items-center justify-center">
      <div className="bg-background-elevated rounded-[24px] mx-[32px]">
        <div className="relative">
          <img
            src="/assets/bettie/bettie_promotion_popup.png"
            alt="bettie promotion popup"
            className="w-full h-full object-cover rounded-t-[24px]"
          />
          <p className="absolute bottom-[30px] left-[50%] translate-x-[-50%] text-[16px] font-medium text-[#3F4254] leading-[22px] w-full text-center whitespace-pre-line">
            <Trans
              i18nKey="ChatPage.bettiePopup.freeHeartPopup"
              components={{
                span: <span className="text-primary-light dark:text-primary-dark font-bold" />
              }}
            />
          </p>
        </div>
        <div className="py-[20px] px-[14px] flex flex-col gap-[16px] items-center">
          <Button size="large" className="w-full text-[17px] font-semibold" onClick={onClose}>
            {t('confirm')}
          </Button>
          <button
            type="button"
            className="text-[14px] text-text-main-lv4 leading-[17px] underline"
            style={{ textUnderlineOffset: '2px' }}
            onClick={handleCloseForever}
          >
            {t('ChatPage.bettiePopup.neverShow')}
          </button>
        </div>
      </div>
    </div>
  );
};
